import { yyyy } from "../../store.svelte";

const SOURCE_ID = "chronicle-diff";

export const LAYER_IDS = [
	"chronicle-diff-added-nodes",
	"chronicle-diff-removed-nodes",
	"chronicle-diff-modified-nodes",
	"chronicle-diff-added-edges",
	"chronicle-diff-removed-edges",
	"chronicle-diff-modified-edges",
];

const COLORS = {
	added: "#22863a",
	removed: "#cb2431",
	modified: "#b08800",
};

let activeOld: string | null = null;
let activeNew: string | null = null;
let fetchController: AbortController | null = null;

export const diffState = $state({ features: [] as any[], loading: false });

function boundsToGeoJSON(map: any) {
	const b = map.getBounds();
	return JSON.stringify({
		type: "Polygon",
		coordinates: [[
			[b.getWest(), b.getSouth()],
			[b.getEast(), b.getSouth()],
			[b.getEast(), b.getNorth()],
			[b.getWest(), b.getNorth()],
			[b.getWest(), b.getSouth()],
		]],
	});
}

async function fetchDiff(map: any) {
	if (!activeOld || !activeNew) return;

	if (fetchController) fetchController.abort();
	fetchController = new AbortController();

	diffState.loading = true;
	const url = yyyy.uri.api;
	const bounds = boundsToGeoJSON(map);

	try {
		const res = await fetch(`${url}/rpc/chronicle_diff_viewport`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: "Bearer " + yyyy.sesh.logtoken.token,
			},
			body: JSON.stringify({
				old_wireident: activeOld,
				new_wireident: activeNew,
				bounds: bounds,
			}),
			signal: fetchController.signal,
		});

		if (!res.ok) throw new Error(`Diff viewport: ${res.statusText}`);
		const rows: any[] = await res.json();

		diffState.features = rows;

		const fc: GeoJSON.FeatureCollection = {
			type: "FeatureCollection",
			features: rows.map((r) => ({
				type: "Feature" as const,
				geometry: r.geom,
				properties: {
					diff_status: r.diff_status,
					feature_type: r.feature_type,
					lw_table: r.lw_table,
					lw_table_pkid: r.lw_table_pkid,
					phase: r.phase,
					feederid: r.feederid,
					diff_detail: r.diff_detail,
				},
			})),
		};

		const src = map.getSource(SOURCE_ID);
		if (src) {
			src.setData(fc);
		}
	} catch (err: any) {
		if (err.name !== "AbortError") console.error("Chronicle diff fetch error:", err);
	} finally {
		diffState.loading = false;
	}
}

export function showChronicleDiff(map: any, oldWireident: string, newWireident: string) {
	activeOld = oldWireident;
	activeNew = newWireident;

	// Add source if not exists
	if (!map.getSource(SOURCE_ID)) {
		map.addSource(SOURCE_ID, {
			type: "geojson",
			data: { type: "FeatureCollection", features: [] },
		});

		// Edge layers (lines — render below nodes)
		map.addLayer({
			id: "chronicle-diff-added-edges",
			type: "line",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "added"], ["==", ["get", "feature_type"], "edge"]],
			paint: { "line-color": COLORS.added, "line-width": 3, "line-opacity": 0.85 },
		});
		map.addLayer({
			id: "chronicle-diff-removed-edges",
			type: "line",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "removed"], ["==", ["get", "feature_type"], "edge"]],
			paint: { "line-color": COLORS.removed, "line-width": 3, "line-opacity": 0.85 },
		});
		map.addLayer({
			id: "chronicle-diff-modified-edges",
			type: "line",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "modified"], ["==", ["get", "feature_type"], "edge"]],
			paint: { "line-color": COLORS.modified, "line-width": 3, "line-opacity": 0.85 },
		});

		// Node layers (circles — render on top)
		map.addLayer({
			id: "chronicle-diff-added-nodes",
			type: "circle",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "added"], ["==", ["get", "feature_type"], "node"]],
			paint: { "circle-color": COLORS.added, "circle-radius": 5, "circle-stroke-width": 1, "circle-stroke-color": "#fff" },
		});
		map.addLayer({
			id: "chronicle-diff-removed-nodes",
			type: "circle",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "removed"], ["==", ["get", "feature_type"], "node"]],
			paint: { "circle-color": COLORS.removed, "circle-radius": 5, "circle-stroke-width": 1, "circle-stroke-color": "#fff" },
		});
		map.addLayer({
			id: "chronicle-diff-modified-nodes",
			type: "circle",
			source: SOURCE_ID,
			filter: ["all", ["==", ["get", "diff_status"], "modified"], ["==", ["get", "feature_type"], "node"]],
			paint: { "circle-color": COLORS.modified, "circle-radius": 5, "circle-stroke-width": 1, "circle-stroke-color": "#fff" },
		});
	}

	fetchDiff(map);
}

export function refreshChronicleDiff(map: any) {
	if (activeOld && activeNew) fetchDiff(map);
}

export function clearChronicleDiff(map: any) {
	activeOld = null;
	activeNew = null;
	diffState.features = [];
	if (fetchController) fetchController.abort();

	for (const id of LAYER_IDS) {
		if (map.getLayer(id)) map.removeLayer(id);
	}
	if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}
